import type { CollectionKey } from "../../../netlify/functions/_shared/types";

export type FieldType = "text" | "textarea" | "number" | "boolean" | "select" | "object";

export interface FieldSchema {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  optionsSource?: {
    collection: Exclude<CollectionKey, "festivalInfo">;
    labelField: string; // The property to show as display label (e.g. 'name')
  };
  properties?: FieldSchema[]; // For type: "object"
}

export const entitySchemas: Record<Exclude<CollectionKey, "festivalInfo">, FieldSchema[]> = {
  artists: [
    { key: "id", label: "ID", type: "text", required: true },
    { key: "name", label: "Name", type: "text", required: true },
    { key: "qualifier", label: "Qualifier (e.g. live, dj set, aka. ...)", type: "text" },
    { key: "collective", label: "Collective", type: "text" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "genre", label: "Genre", type: "text" },
    { key: "image", label: "Image URL", type: "text" },
    { key: "playerUrl", label: "Player URL (SoundCloud, Spotify, or YouTube)", type: "text" },
  ],
  days: [
    { key: "id", label: "ID", type: "text", required: true },
    { key: "date", label: "Date (YYYY-MM-DD)", type: "text", required: true },
    { key: "name", label: "Name", type: "text", required: true },
    { key: "description", label: "Description", type: "textarea" },
    { key: "facebookEvent", label: "Facebook Event URL", type: "text" },
    { key: "ticketUrl", label: "Ticket URL", type: "text" },
    {
      key: "themeColors",
      label: "Theme Colors",
      type: "object",
      properties: [
        { key: "primary", label: "Primary Color", type: "text", required: true },
        { key: "secondary", label: "Secondary Color", type: "text", required: true },
        { key: "background", label: "Background Color", type: "text", required: true },
        { key: "text", label: "Text Color", type: "text", required: true },
      ],
    },
  ],
  stages: [
    { key: "id", label: "ID", type: "text", required: true },
    { key: "name", label: "Name", type: "text", required: true },
    { key: "description", label: "Description", type: "textarea" },
    { key: "icon", label: "Icon Emoji / Symbol", type: "text" },
    { key: "image", label: "Image URL", type: "text" },
    { key: "offsetX", label: "Map Offset X", type: "number" },
    { key: "offsetY", label: "Map Offset Y", type: "number" },
    { key: "hidden", label: "Hidden Stage", type: "boolean" },
  ],
  performances: [
    { key: "id", label: "ID", type: "text", required: true },
    {
      key: "artistId",
      label: "Artist",
      type: "select",
      required: true,
      optionsSource: { collection: "artists", labelField: "name" },
    },
    {
      key: "dayId",
      label: "Day / Theme",
      type: "select",
      required: true,
      optionsSource: { collection: "days", labelField: "name" },
    },
    {
      key: "stageId",
      label: "Stage",
      type: "select",
      required: true,
      optionsSource: { collection: "stages", labelField: "name" },
    },
    { key: "description", label: "Description Override", type: "textarea" },
    { key: "startTime", label: "Start Time (HH:MM)", type: "text", required: true },
    { key: "endTime", label: "End Time (HH:MM)", type: "text", required: true },
  ],
};
